import axios, { AxiosRequestConfig } from 'axios'
import { useCallback, useState } from 'react'

import { useOktaAuth } from '@okta/okta-react'

import { getApiFromEnv } from '../utilities'

export type LoadingStatus = 'INIT' | 'ERROR' | 'LOADING' | 'SUCCESS'

const useAxios = () => {
  const { authState } = useOktaAuth()
  const BASE_URL = getApiFromEnv()
  const AUTH_TOKEN = authState?.accessToken?.accessToken
  const TENANT_ID = authState?.accessToken?.claims.tenantId

  const authBearer =
    TENANT_ID !== null
      ? `Bearer ${AUTH_TOKEN}:${TENANT_ID}`
      : `Bearer ${AUTH_TOKEN}`

  return axios.create({
    baseURL: BASE_URL,
    headers: {
      Authorization: authBearer,
    },
  })
}

export const useAxiosWithErrorHandling = () => {
  const axios = useAxios()

  return <T>(config: AxiosRequestConfig): Promise<T | undefined> =>
    axios
      .request<T>(config)
      .then((response) => response.data)
      .catch((error) => {
        console.error(error)
        return undefined
      })
}

export const useObservableAxios = <T>(): ParameterizedObservableRequest<
  T,
  AxiosRequestConfig
> => {
  const axios = useAxios()

  const [status, setStatus] = useState<LoadingStatus>('INIT')
  const [error, setError] = useState<Error | undefined>()
  const [result, setResult] = useState<T | undefined>()

  const axiosWrapper = useCallback(
    (config: AxiosRequestConfig): Promise<void> => {
      setStatus('LOADING')
      return axios
        .request<T>(config)
        .then((response) => {
          setStatus('SUCCESS')
          setResult(response.data)
        })
        .catch((error) => {
          setStatus('ERROR')
          setError(error)
        })
    },
    [axios]
  )

  return [axiosWrapper, status, error, result]
}

export type ParameterizedObservableRequest<R, P = void> = [
  (param: P) => Promise<void>,
  LoadingStatus,
  Error | undefined,
  R | undefined
]

export type ParameterizedRequest<R, P = void> = (
  param: P
) => Promise<R | undefined>

export default useAxios
